import { useQuery } from '@tanstack/react-query'
import type { Tables } from '@quoter/shared/generated/db-types'
import { supabase } from '../lib/supabase'
import { useSession } from '../session/useSession'

export type Member = Pick<Tables<'profiles'>, 'id' | 'email' | 'full_name' | 'role' | 'active'>

/**
 * Everyone in the caller's org, from memberships (the platform's user table).
 * Filtered by org explicitly: RLS admits every org the user belongs to across
 * products.
 */
export function useMembers() {
  const session = useSession()
  const orgId = session.status === 'authenticated' ? session.organizationId : null

  const query = useQuery({
    queryKey: ['members', orgId ?? '__none'],
    enabled: !!orgId,
    staleTime: 300_000,
    queryFn: async (): Promise<Member[]> => {
      const { data, error } = await supabase
        .from('memberships')
        .select('user_id, email, full_name, role, active')
        .eq('organization_id', orgId!)
        .eq('active', true)
        .order('full_name')
      if (error) throw error
      return (data ?? []).map(({ user_id, ...rest }) => ({ id: user_id, ...rest }))
    },
  })

  return { members: query.data ?? [], loading: query.isLoading, error: query.error }
}

/** Falls back to the email local-part so an unnamed member still reads as a person. */
export function memberLabel(member: Pick<Member, 'full_name' | 'email'>): string {
  return member.full_name?.trim() || member.email.split('@')[0] || member.email
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}
